import { useState, useEffect } from 'react';
import { Match } from '../models/interfaces/Match';
import { MatchEntry } from '../models/interfaces/MatchEntry';
import { RosterEntry } from '../models/interfaces/RosterEntry';

interface Props {
    team: MatchEntry;
    match: Match;
}

export default function useGetMatchEntryPlayers({ team, match }: Props) {
    const [teamPlayers, setPlayers] = useState<RosterEntry[] | undefined>(undefined);
    const [resetPlayerList, setResetPlayerList] = useState<boolean>(false);

    function triggerUpdatedPlayerList() {
        setResetPlayerList(true);
    }

    useEffect(() => {
        // this if statement is used to trigger a reload of the useEffect & subsequently get an updated roster provider if a player has been updated (an new player or deleted player already trigger a reset)
        if (resetPlayerList) {
            setResetPlayerList(false);
        }
        const listenerToken = team.rosterProvider.addListener((updatedResolvers) => {
            const playerPromises = updatedResolvers.map((resolver) => {
                return new Promise<RosterEntry | undefined>((resolve) => {
                    resolver.resolve(
                        (player) => {
                            match.capChangeForPlayer(player).then((updatedCapNumber) => {
                                if (updatedCapNumber) {
                                    const playerCopy: RosterEntry = {
                                        ...player,
                                        capNumber: updatedCapNumber,
                                    };
                                    resolve(playerCopy);
                                } else {
                                    resolve(player);
                                }
                            });
                        },
                        (errorMessage) => {
                            resolve(undefined);
                        }
                    );
                });
            });
            Promise.all(playerPromises).then((resolvedPromises) => {
                const updatedPlayers = resolvedPromises.reduce((currentPlayers, resolvedObject) => {
                    if (resolvedObject) {
                        return currentPlayers.concat([resolvedObject]);
                    } else {
                        return currentPlayers;
                    }
                }, new Array<RosterEntry>());
                const sortedPlayers = updatedPlayers.sort((a, b) => {
                    if (a.isArchived && !b.isArchived) {
                        return 1;
                    } else if (!a.isArchived && b.isArchived) {
                        return -1;
                    } else {
                        return a.capNumber.sortValue - b.capNumber.sortValue;
                    }
                });
                setPlayers(sortedPlayers);
            });
        });
        return () => {
            team.rosterProvider.removeListener(listenerToken);
        };
    }, [team.rosterProvider, resetPlayerList]);

    return { teamPlayers, triggerUpdatedPlayerList };
}
